import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadPages } from '@common/redux/slice/page';
import { fetchPages } from '@common/services';
import { IAppInfo, IPage } from '@common/types';
import { appUseMap } from '@common/utils/component';
import { fetchTemplateById } from '@common/services';
import { deepMerge } from '@common/utils/object';
import { dimensionSelector } from '@common/redux/selectors/page';
import { Platform } from 'click-types';
import { getMetadataScreenByResponsive } from '@common/utils/screen';
// import AsyncStorage from '@react-native-async-storage/async-storage';
// import { TOKEN_KEY } from '@common/constants';
// import { chunk } from 'lodash';

type APIResponse = {
  data: any;
  error: string | null;
  isLoading: boolean;
  isUseMap: boolean;
};

const EXPIRIES = 'cacheExpiresOn';
const SCREEN_LOCAL = 'screenLocal';

export const getAllPages = (
  appId: string,
  appInfo: (IAppInfo & { brandAssets?: Record<string, any> }) | undefined
) => {
  const dispatch = useDispatch();
  const dimension = useSelector(dimensionSelector);
  const platform = appInfo?.platform;
  const [response, setResponse] = useState<APIResponse>({
    data: [],
    error: null,
    isLoading: true,
    isUseMap: false,
  });

  const _fetchPages = useCallback(async () => {
    setResponse((prev) => ({ ...prev, isLoading: true }));
    try {
      const params = {};

      // const expiresOnLocal = await AsyncStorage.getItem(EXPIRIES);
      // const expiresOn = appInfo?.cacheExpiresOn;
      // const keys = await AsyncStorage.getAllKeys();
      let pages: any[] = [];
      if (appInfo) {
        const pageData = await fetchPages(appId, params);
        pages = pageData.data;

        const getPageTemplates = async () => {
          return Promise.all(
            pages.map(async (page) => {
              if (page.screenTemplate !== null) {
                const primary = page?.metadata?.[0];
                const pageTemplateId = primary?.pageTemplateId;

                if (primary && pageTemplateId) {
                  const template = await fetchTemplateById(
                    appInfo.appId,
                    pageTemplateId
                  );
                  if (!template) {
                    return page;
                  }

                  const breakpointManifest = page?.metadata?.map(
                    (breakpoint: {
                      key: string;
                      style: Partial<any> | undefined;
                    }) => {
                      if (!template.metadata) {
                        return breakpoint;
                      }

                      const style = deepMerge(
                        template.metadata.style,
                        breakpoint.style
                      );
                      return { ...style, key: breakpoint.key };
                    }
                  );

                  page = {
                    ...page,
                    metadata: [
                      {
                        ...template.metadata,
                        pageTemplateId: template.id,
                        breakpointManifest,
                      },
                    ],
                  };
                  return page;
                }
              }

              return page;
            })
          );
        };

        pages = await getPageTemplates();

        // if (expiresOnLocal !== expiresOn) {
        //   console.log('fetch api ---- ');
        //   const pageData = await fetchPages(appId, params);
        //   pages = pageData.data;

        //   // remove all token before saving
        //   const keyNoToken = keys.filter((i) => i != TOKEN_KEY);
        //   AsyncStorage.multiRemove(keyNoToken);
        //   // save pages to localstorage
        //   await AsyncStorage.setItem(EXPIRIES, pageData.expiresOn);
        //   const chunkPages = chunk(pages, 20);
        //   for (let i = 0; i < chunkPages.length; i++) {
        //     let screenLocal = 'screenLocal' + i;
        //     let page = chunkPages[i];
        //     //save first user
        //     await AsyncStorage.setItem(screenLocal, JSON.stringify(page));
        //   }
        // } else {
        //   console.log('get local ---- ');
        //   for (let key of keys) {
        //     if (key.includes('screenLocal')) {
        //       let val = (await AsyncStorage.getItem(key)) || '[]';
        //       // parse JSON
        //       let page = JSON.parse(val);
        //       pages.push(...page);
        //     }
        //   }
        // }
      }

      // update page by responsive design
      const pageMaps: { [key: string]: IPage } = pages.reduce(
        (acc, page: IPage) => ({ ...acc, [page.screenUuid]: page }),
        {}
      );
      if (platform === Platform.RESPONSIVE) {
        const newPages: { [key: string]: IPage } = {};
        Object.keys(pageMaps).forEach((pageId) => {
          const page = pageMaps[pageId];

          if (
            page.screenTemplate !== null &&
            page.screenTemplate !== 'component'
          ) {
            return (newPages[pageId] = page);
          }

          const metadata = getMetadataScreenByResponsive(page, dimension);
          newPages[pageId] = {
            ...page,
            ...metadata,
            metadata: metadata.children ?? [],
          };
        });
        dispatch(loadPages(newPages));
      } else {
        dispatch(loadPages(pageMaps));
      }

      const isUseMap = appUseMap(pages);
      setResponse((prev) => ({
        ...prev,
        isUseMap,
        isLoading: false,
        data: pages,
      }));
    } catch (error: any) {
      console.error('===fetch pages error', error);
      setResponse((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message || 'get data error',
      }));
    }
  }, [appId, appInfo, dimension]);

  useEffect(() => {
    _fetchPages();
  }, [_fetchPages]);

  return response;
};
